// src/components/signInDropdown/SideNavigation.jsx - Updated
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const SideNavigation = ({ isDarkMode, toggleDarkMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine active page based on current route with more precise matching
  const getActivePage = () => {
    const pathname = location.pathname;
    
    // Check for settings-related routes first (including profile and team management)
    if (pathname.startsWith('/profile') || 
        pathname.startsWith('/system-access') || 
        pathname.startsWith('/team-management') ||
        pathname.includes('settings')) return 'settings';
    
    // Check for other specific routes
    if (pathname.startsWith('/store')) return 'store';
    if (pathname.startsWith('/mybots')) return 'mybots';
    if (pathname.startsWith('/billing')) return 'billing';
    
    // Exact match for home routes
    if (pathname === '/mainscreen' || pathname === '/mainscreen/') return 'home';
    
    // If we're in any mainscreen sub-route but not the root, don't highlight home
    if (pathname.startsWith('/mainscreen/')) return null;
    
    // Default fallback for home
    return 'home';
  };

  const activePage = getActivePage();
  
  const navigationItems = [
    {
      id: 'home',
      label: 'Home',
      icon: (
        <svg className="w-6 h-6 mb-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 3L2 12H5V20H10V14H14V20H19V12H22L12 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      route: '/mainscreen'
    },
    {
      id: 'store',
      label: 'Bot Store',
      icon: (
        <svg className="w-6 h-6 mb-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
          <circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="15" cy="15" r="1" fill="currentColor"/>
          <circle cx="18" cy="12" r="1" fill="currentColor"/>
        </svg>
      ),
      route: '/store'
    },
    {
      id: 'mybots',
      label: 'My Bots',
      icon: (
        <svg className="w-6 h-6 mb-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
          <circle cx="8" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M14 10H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M14 14H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <rect x="2" y="2" width="20" height="2" rx="1" fill="currentColor"/>
        </svg>
      ),
      route: '/mybots'
    },
    {
      id: 'billing',
      label: 'Billing & Usage',
      icon: (
        <svg className="w-6 h-6 mb-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M2 8H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M6 16H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M10 16H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="18" cy="14" r="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M18 12V16" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
        </svg>
      ),
      route: '/billing'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: (
        <svg className="w-6 h-6 mb-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
          <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.87653 6.85425 4.02405 7.04 4.21L7.1 4.27C7.33568 4.50054 7.63502 4.65519 7.95941 4.714C8.28381 4.77282 8.61838 4.73312 8.92 4.6H9C9.29577 4.47324 9.54802 4.26276 9.72569 3.99447C9.90337 3.72618 9.99872 3.41179 10 3.09V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.75523 19.0609 3.85588C19.3037 3.95653 19.5243 4.10405 19.71 4.29C19.896 4.47575 20.0435 4.69632 20.1441 4.93912C20.2448 5.18192 20.2966 5.44217 20.2966 5.705C20.2966 5.96783 20.2448 6.22808 20.1441 6.47088C20.0435 6.71368 19.896 6.93425 19.71 7.12L19.65 7.18C19.4195 7.41568 19.2648 7.71502 19.206 8.03941C19.1472 8.36381 19.1869 8.69838 19.32 9V9.08C19.4468 9.37577 19.6572 9.62802 19.9255 9.80569C20.1938 9.98337 20.5082 10.0787 20.83 10.08H21C21.5304 10.08 22.0391 10.2907 22.4142 10.6658C22.7893 11.0409 23 11.5496 23 12.08C23 12.6104 22.7893 13.1191 22.4142 13.4942C22.0391 13.8693 21.5304 14.08 21 14.08H20.91C20.5882 14.0813 20.2738 14.1766 20.0055 14.3543C19.7372 14.532 19.5268 14.7842 19.4 15.08Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      route: '/profile'
    }
  ];

  const handleNavClick = (item) => {
    console.log(`Navigate to: ${item.id}`);
    navigate(item.route);
  };

  return (
    <nav className={`
      fixed top-[70px] left-0 w-20 h-[calc(100vh-70px)]
      border-r transition-colors duration-300 flex flex-col z-90
      ${isDarkMode 
        ? 'bg-[#0D0B1F] border-gray-700 shadow-black/30' 
        : 'bg-[#100E27] border-gray-200 shadow-gray-900/10'
      }
      shadow-lg
    `}>
      {/* Navigation Items */}
      <div className="pt-5 px-2">
        {navigationItems.map((item) => {
          const isActive = activePage === item.id;
          
          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item)}
              className={`
                flex flex-col items-center justify-center
                w-full py-4 mb-2 rounded-lg
                text-white/70 hover:text-white/90
                transition-all duration-200 ease-in-out
                hover:bg-white/8 focus:outline-none focus:ring-2 focus:ring-white/20
                relative
                ${isActive 
                  ? `bg-white/15 text-white` 
                  : ''
                }
              `}
            >
              {/* Active indicator line - normal theme color */}
              {isActive && (
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${isDarkMode ? 'bg-[#7376B8]' : 'bg-white'}`} />
              )}
              
              {/* Icon */}
              <div className="mb-1.5">
                {item.icon}
              </div>
              
              {/* Label */}
              <span className={`
                text-[10px] font-medium text-center leading-tight
                tracking-wide
                ${isActive ? 'text-white' : ''}
              `}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Dark Mode Toggle at bottom */}
      <div className="mt-auto mb-4 px-2">
        <button
          onClick={toggleDarkMode}
          className={`
            flex flex-col items-center justify-center
            w-full py-4 rounded-lg
            text-white/70 hover:text-white/90
            transition-all duration-200 ease-in-out
            hover:bg-white/8 focus:outline-none focus:ring-2 focus:ring-white/20
          `}
          aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {/* Icon */}
          <div className="mb-1.5">
            {isDarkMode ? (
              // Sun icon for light mode
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22" 
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              // Moon icon for dark mode
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" 
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          
          {/* Label */}
          <span className="text-[10px] font-medium text-center leading-tight tracking-wide">
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </span>
        </button>
      </div>
    </nav>
  );
};

export default SideNavigation;